// src/hooks/useOrderKarigar.js
// ============================================================================
// ENHANCED ORDER-KARIGAR BRIDGE HOOK
// ============================================================================
// This hook provides a unified interface for karigar operations by combining:
//
// 1. ORDER SLICE (state.orders):
//    - order.karigarAssignments - Direct from order object
//    - order.karigarWorkStatus - Summary from order object
//    - order.karigarJobs - Jobs fetched via order routes
//    - NEW thunks: getOrderKarigarJobsThunk, createBulkOrderKarigarJobsThunk, etc.
//
// 2. ORDER KARIGAR SLICE (state.orderKarigar):
//    - availableKarigars - From /order-item-karigar/available-karigars
//    - orderStatus - From /order-item-karigar/order/:orderId/status
//    - karigarQueue - From /order-item-karigar/karigar/:karigarId/queue
//    - Assignment operations - assign, reassign, bulk-assign
//
// Backend Routes:
// ORDER SERVICE (new):
//   GET    /orders/admin/order/:orderId/karigar-jobs
//   POST   /orders/admin/order/:orderId/karigar-jobs
//   POST   /orders/admin/order/:orderId/karigar-jobs/bulk
//   PATCH  /orders/admin/order/:orderId/karigar-jobs/:jobId
//   DELETE /orders/admin/order/:orderId/karigar-jobs/:jobId
//   GET    /orders/admin/order/:orderId/karigar-summary
//   GET    /orders/admin/order/:orderId/karigar-readiness
//   POST   /orders/admin/order/:orderId/complete-karigar-jobs
//
// ORDER-ITEM-KARIGAR SERVICE (existing):
//   POST   /order-item-karigar/available-karigars
//   POST   /order-item-karigar/assign
//   POST   /order-item-karigar/reassign
//   POST   /order-item-karigar/bulk-assign
//   GET    /order-item-karigar/order/:orderId/status
//   GET    /order-item-karigar/karigar/:karigarId/queue
// ============================================================================

import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

// ============================================================================
// IMPORTS - ORDER SLICE THUNKS (NEW)
// ============================================================================
import {
  // Karigar job management via order routes
  getOrderKarigarJobsThunk,
  createOrderKarigarJobThunk,
  createBulkOrderKarigarJobsThunk,
  updateOrderKarigarJobThunk,
  cancelOrderKarigarJobThunk,
  getOrderKarigarSummaryThunk,
  checkKarigarReadinessThunk,
  forceCompleteKarigarJobsThunk,
} from "../store/thunks/orderThunks";

// ============================================================================
// IMPORTS - ORDER KARIGAR SLICE THUNKS (EXISTING)
// ============================================================================
import {
  fetchAvailableKarigarsForOrderItem,
  fetchBulkKarigarAvailability,
  assignKarigarToOrderItem,
  reassignKarigarForOrderItem,
  bulkAssignKarigars,
  getOrderKarigarStatus,
  getKarigarQueue,
  checkOrderCanFulfill,
  assignKarigarViaOrder,
} from "../store/thunks/karigar/orderKarigarThunks";

// ============================================================================
// IMPORTS - ORDER KARIGAR SLICE ACTIONS
// ============================================================================
import {
  clearAvailableKarigars,
  clearOrderStatus,
  clearKarigarQueue,
  clearCanFulfillCheck,
  clearLastAssignment,
  clearLastReassignment,
  clearBulkAssignmentResults,
  clearError,
  clearSuccessMessage,
  clearAll,
} from "../store/slices/karigar/orderKarigarSlice";

// ============================================================================
// IMPORTS - ORDER SLICE UI ACTIONS
// ============================================================================
import {
  openKarigarAssignmentModal,
  closeKarigarAssignmentModal,
  openKarigarStatusPanel,
  closeKarigarStatusPanel,
  openKarigarQueueModal,
  closeKarigarQueueModal,
  clearKarigarUI,
  clearKarigarJobs, // NEW action
} from "../store/slices/orderSlice";

// ============================================================================
// IMPORTS - ORDER KARIGAR SELECTORS
// ============================================================================
import {
  selectAvailableKarigars,
  selectProductKarigars,
  selectOtherKarigars,
  selectAllAvailableKarigars,
  selectKarigarRecommendations,
  selectAvailabilitySummary,
  selectOrderKarigarStatus,
  selectHasKarigarWork,
  selectOrderJobs,
  selectCanFulfillOrder,
  selectKarigarQueueState,
  selectQueueJobsList,
  selectCanFulfillCheck,
  selectLastAssignment,
  selectOrderKarigarLoading,
  selectOrderKarigarError,
  selectOrderKarigarSuccessMessage,
} from "../store/selectors/orderKarigarSelectors";

// ============================================================================
// IMPORTS - ORDER SELECTORS (karigar data in order object)
// ============================================================================
import {
  selectOrderKarigarAssignments,
  selectOrderKarigarWorkStatus,
  selectOrderItemsWithoutKarigar,
  selectOrderItemsWithKarigar,
  selectCanAssignKarigarToOrder,
  selectAllItemsHaveKarigar,
  selectHasAnyKarigarAssigned,
  selectItemsNeedingKarigarCount,
  selectAllKarigarWorkComplete,
  selectCanFulfillBasedOnKarigar,
  selectKarigarUI,
  selectShowKarigarAssignmentModal,
  selectSelectedItemForKarigar,
  selectSelectedProductForKarigar,
  selectShowKarigarStatusPanel,
  selectShowKarigarQueueModal,
  selectSelectedKarigarForQueue,
  selectOrderWithKarigarSummary,
  selectItemsWithKarigarStatus,
} from "../store/selectors/orderSelectors";

// ============================================================================
// HOOK: useOrderKarigar
// ============================================================================

export const useOrderKarigar = () => {
  const dispatch = useDispatch();

  // ========================================================================
  // SELECTORS - Order Karigar Data (from orders.order)
  // Data stored directly in order.karigarAssignments, order.karigarWorkStatus
  // ========================================================================
  const orderKarigarAssignments = useSelector(selectOrderKarigarAssignments);
  const orderWorkStatus = useSelector(selectOrderKarigarWorkStatus);
  const itemsWithoutKarigar = useSelector(selectOrderItemsWithoutKarigar);
  const itemsWithKarigar = useSelector(selectOrderItemsWithKarigar);
  const canAssignKarigar = useSelector(selectCanAssignKarigarToOrder);
  const allItemsHaveKarigar = useSelector(selectAllItemsHaveKarigar);
  const hasAnyKarigarAssigned = useSelector(selectHasAnyKarigarAssigned);
  const itemsNeedingKarigarCount = useSelector(selectItemsNeedingKarigarCount);
  const allKarigarWorkComplete = useSelector(selectAllKarigarWorkComplete);
  const canFulfillBasedOnKarigar = useSelector(selectCanFulfillBasedOnKarigar);
  const orderWithKarigarSummary = useSelector(selectOrderWithKarigarSummary);
  const itemsWithKarigarStatus = useSelector(selectItemsWithKarigarStatus);

  // ========================================================================
  // SELECTORS - Karigar Jobs (from orders.karigarJobs - NEW)
  // Data fetched via getOrderKarigarJobsThunk
  // ========================================================================
  const karigarJobsState = useSelector(
    (state) =>
      state.orders?.karigarJobs || {
        list: [],
        summary: null,
        readiness: null,
        loading: false,
        error: null,
        itemJobMapping: [],
      }
  );

  // ========================================================================
  // SELECTORS - Available Karigars (from orderKarigar slice)
  // Result of fetchAvailableKarigarsForOrderItem / fetchBulkKarigarAvailability
  // ========================================================================
  const availableKarigarsData = useSelector(selectAvailableKarigars);
  const bulkProductKarigars = useSelector(selectProductKarigars);
  const bulkOtherKarigars = useSelector(selectOtherKarigars);
  const allAvailableKarigars = useSelector(selectAllAvailableKarigars);
  const bulkRecommendations = useSelector(selectKarigarRecommendations);
  const bulkSummary = useSelector(selectAvailabilitySummary);

  // ========================================================================
  // SELECTORS - Order Karigar Status (from orderKarigar slice)
  // Result of getOrderKarigarStatus
  // ========================================================================
  const orderKarigarStatusFromSlice = useSelector(selectOrderKarigarStatus);
  const hasKarigarWork = useSelector(selectHasKarigarWork);
  const orderJobs = useSelector(selectOrderJobs);
  const canFulfillOrder = useSelector(selectCanFulfillOrder);

  // ========================================================================
  // SELECTORS - Karigar Queue (from orderKarigar slice)
  // ========================================================================
  const karigarQueueState = useSelector(selectKarigarQueueState);
  const queueJobsList = useSelector(selectQueueJobsList);
  const karigarQueue = useMemo(
    () => ({
      ...karigarQueueState,
      jobs: queueJobsList,
    }),
    [karigarQueueState, queueJobsList]
  );

  // ========================================================================
  // SELECTORS - Can Fulfill Check & Assignment Results
  // ========================================================================
  const canFulfillCheck = useSelector(selectCanFulfillCheck);
  const lastAssignment = useSelector(selectLastAssignment);

  // ========================================================================
  // SELECTORS - Karigar UI State (from orders slice)
  // ========================================================================
  const karigarUI = useSelector(selectKarigarUI);
  const showAssignmentModal = useSelector(selectShowKarigarAssignmentModal);
  const selectedItemIndex = useSelector(selectSelectedItemForKarigar);
  const selectedProductId = useSelector(selectSelectedProductForKarigar);
  const showStatusPanel = useSelector(selectShowKarigarStatusPanel);
  const showQueueModal = useSelector(selectShowKarigarQueueModal);
  const selectedKarigarId = useSelector(selectSelectedKarigarForQueue);

  // ========================================================================
  // SELECTORS - Loading States (combined from both slices)
  // ========================================================================
  const orderKarigarLoading = useSelector(selectOrderKarigarLoading);
  const loading = useMemo(
    () => ({
      ...orderKarigarLoading,
      // Add order slice karigar loading states
      karigarJobs: karigarJobsState.loading,
    }),
    [orderKarigarLoading, karigarJobsState.loading]
  );

  // ========================================================================
  // SELECTORS - Error & Success
  // ========================================================================
  const error = useSelector(selectOrderKarigarError) || karigarJobsState.error;
  const successMessage = useSelector(selectOrderKarigarSuccessMessage);

  // ========================================================================
  // ACTIONS - Get Available Karigars (orderKarigar slice)
  // ========================================================================
  const getAvailableKarigars = useCallback(
    (payload) => dispatch(fetchAvailableKarigarsForOrderItem(payload)),
    [dispatch]
  );

  const getBulkAvailability = useCallback(
    (payload) => dispatch(fetchBulkKarigarAvailability(payload)),
    [dispatch]
  );

  // ========================================================================
  // ACTIONS - Assignment via orderKarigar slice
  // ========================================================================
  const assignToOrderItem = useCallback(
    (payload) => dispatch(assignKarigarToOrderItem(payload)),
    [dispatch]
  );

  const assignViaOrder = useCallback(
    (orderId, itemIndex, karigarId, workDetails = {}) =>
      dispatch(
        assignKarigarViaOrder({
          orderId,
          itemIndex,
          karigarId,
          workDetails,
        })
      ),
    [dispatch]
  );

  const reassignForOrderItem = useCallback(
    (orderId, orderItemIndex, newKarigarId, reason, adminInfo = {}) =>
      dispatch(
        reassignKarigarForOrderItem({
          orderId,
          orderItemIndex,
          newKarigarId,
          reason,
          adminInfo,
        })
      ),
    [dispatch]
  );

  const bulkAssign = useCallback(
    (payload) => dispatch(bulkAssignKarigars(payload)),
    [dispatch]
  );

  // ========================================================================
  // ACTIONS - NEW Order-based Karigar Job Management
  // These use the new routes: /orders/admin/order/:orderId/karigar-jobs/*
  // ========================================================================

  /**
   * Get all karigar jobs for an order
   * GET /orders/admin/order/:orderId/karigar-jobs
   */
  const getOrderKarigarJobs = useCallback(
    (orderId) => dispatch(getOrderKarigarJobsThunk(orderId)),
    [dispatch]
  );

  /**
   * Create karigar job for a specific order item
   * POST /orders/admin/order/:orderId/karigar-jobs
   */
  const createOrderKarigarJob = useCallback(
    (orderId, jobData) =>
      dispatch(createOrderKarigarJobThunk({ orderId, jobData })),
    [dispatch]
  );

  /**
   * Create karigar jobs for all order items (bulk)
   * POST /orders/admin/order/:orderId/karigar-jobs/bulk
   */
  const createBulkKarigarJobs = useCallback(
    (orderId, bulkData) =>
      dispatch(createBulkOrderKarigarJobsThunk({ orderId, bulkData })),
    [dispatch]
  );

  /**
   * Update karigar job from order context
   * PATCH /orders/admin/order/:orderId/karigar-jobs/:jobId
   */
  const updateOrderKarigarJob = useCallback(
    (orderId, jobId, updateData) =>
      dispatch(updateOrderKarigarJobThunk({ orderId, jobId, updateData })),
    [dispatch]
  );

  /**
   * Cancel karigar job from order context
   * DELETE /orders/admin/order/:orderId/karigar-jobs/:jobId
   */
  const cancelOrderKarigarJob = useCallback(
    (orderId, jobId, reason) =>
      dispatch(cancelOrderKarigarJobThunk({ orderId, jobId, reason })),
    [dispatch]
  );

  /**
   * Get karigar work summary for order
   * GET /orders/admin/order/:orderId/karigar-summary
   */
  const getOrderKarigarSummary = useCallback(
    (orderId) => dispatch(getOrderKarigarSummaryThunk(orderId)),
    [dispatch]
  );

  /**
   * Check karigar readiness before fulfillment
   * GET /orders/admin/order/:orderId/karigar-readiness
   */
  const checkKarigarReadiness = useCallback(
    (orderId) => dispatch(checkKarigarReadinessThunk(orderId)),
    [dispatch]
  );

  /**
   * Force complete all pending karigar jobs
   * POST /orders/admin/order/:orderId/complete-karigar-jobs
   */
  const forceCompleteKarigarJobs = useCallback(
    (orderId, completionData) =>
      dispatch(forceCompleteKarigarJobsThunk({ orderId, completionData })),
    [dispatch]
  );

  // ========================================================================
  // ACTIONS - Status & Queue via orderKarigar slice
  // ========================================================================
  const getOrderStatus = useCallback(
    (orderId) => dispatch(getOrderKarigarStatus(orderId)),
    [dispatch]
  );

  const getQueue = useCallback(
    (karigarId, includeCompleted = false) =>
      dispatch(getKarigarQueue({ karigarId, includeCompleted })),
    [dispatch]
  );

  const checkCanFulfill = useCallback(
    (orderId) => dispatch(checkOrderCanFulfill(orderId)),
    [dispatch]
  );

  // ========================================================================
  // ACTIONS - UI (from orders slice)
  // ========================================================================
  const openAssignmentModal = useCallback(
    (itemIndex, productId) =>
      dispatch(openKarigarAssignmentModal({ itemIndex, productId })),
    [dispatch]
  );

  const closeAssignmentModal = useCallback(
    () => dispatch(closeKarigarAssignmentModal()),
    [dispatch]
  );

  const openStatusPanel = useCallback(
    () => dispatch(openKarigarStatusPanel()),
    [dispatch]
  );

  const closeStatusPanel = useCallback(
    () => dispatch(closeKarigarStatusPanel()),
    [dispatch]
  );

  const openQueueModal = useCallback(
    (karigarId) => dispatch(openKarigarQueueModal(karigarId)),
    [dispatch]
  );

  const closeQueueModal = useCallback(
    () => dispatch(closeKarigarQueueModal()),
    [dispatch]
  );

  // ========================================================================
  // ACTIONS - Clear State
  // ========================================================================
  const clearAvailable = useCallback(
    () => dispatch(clearAvailableKarigars()),
    [dispatch]
  );

  const clearStatus = useCallback(
    () => dispatch(clearOrderStatus()),
    [dispatch]
  );

  const clearQueue = useCallback(
    () => dispatch(clearKarigarQueue()),
    [dispatch]
  );

  const clearCanFulfillResult = useCallback(
    () => dispatch(clearCanFulfillCheck()),
    [dispatch]
  );

  const clearUI = useCallback(() => dispatch(clearKarigarUI()), [dispatch]);

  const clearErrorMessage = useCallback(
    () => dispatch(clearError()),
    [dispatch]
  );

  const clearSuccess = useCallback(
    () => dispatch(clearSuccessMessage()),
    [dispatch]
  );

  const clearJobs = useCallback(() => dispatch(clearKarigarJobs()), [dispatch]);

  const clearAllState = useCallback(() => {
    dispatch(clearAll());
    dispatch(clearKarigarUI());
    dispatch(clearKarigarJobs());
  }, [dispatch]);

  // ========================================================================
  // RETURN
  // ========================================================================
  return {
    // =====================================================================
    // Order karigar data (from order object - orders.order.karigarAssignments)
    // =====================================================================
    orderKarigarAssignments,
    orderWorkStatus,
    itemsWithoutKarigar,
    itemsWithKarigar,
    canAssignKarigar,
    allItemsHaveKarigar,
    hasAnyKarigarAssigned,
    itemsNeedingKarigarCount,
    allKarigarWorkComplete,
    canFulfillBasedOnKarigar,
    orderWithKarigarSummary,
    itemsWithKarigarStatus,

    // =====================================================================
    // Karigar Jobs (from orders.karigarJobs - fetched via order routes)
    // =====================================================================
    karigarJobs: karigarJobsState.list,
    karigarJobsSummary: karigarJobsState.summary,
    karigarReadiness: karigarJobsState.readiness,
    karigarJobsItemMapping: karigarJobsState.itemJobMapping,
    karigarJobsLoading: karigarJobsState.loading,
    karigarJobsError: karigarJobsState.error,

    // =====================================================================
    // Available karigars (from orderKarigar slice)
    // =====================================================================
    availableKarigarsData,
    bulkProductKarigars,
    bulkOtherKarigars,
    allAvailableKarigars,
    bulkRecommendations,
    bulkSummary,

    // =====================================================================
    // Order karigar status (from orderKarigar slice)
    // =====================================================================
    orderKarigarStatus: orderKarigarStatusFromSlice,
    hasKarigarWork,
    orderJobs,
    canFulfillOrder,
    karigarQueue,
    canFulfillCheck,
    lastAssignment,

    // =====================================================================
    // UI State (from orders slice)
    // =====================================================================
    karigarUI,
    showAssignmentModal,
    selectedItemIndex,
    selectedProductId,
    showStatusPanel,
    showQueueModal,
    selectedKarigarId,

    // =====================================================================
    // Loading states (combined)
    // =====================================================================
    loading,

    // =====================================================================
    // Error & success
    // =====================================================================
    error,
    successMessage,

    // =====================================================================
    // Actions - Get available karigars (orderKarigar slice)
    // =====================================================================
    getAvailableKarigars,
    getBulkAvailability,

    // =====================================================================
    // Actions - Assignment (orderKarigar slice)
    // =====================================================================
    assignToOrderItem,
    assignViaOrder,
    reassignForOrderItem,
    bulkAssign,

    // =====================================================================
    // Actions - NEW Order-based Karigar Job Management
    // =====================================================================
    getOrderKarigarJobs,
    createOrderKarigarJob,
    createBulkKarigarJobs,
    updateOrderKarigarJob,
    cancelOrderKarigarJob,
    getOrderKarigarSummary,
    checkKarigarReadiness,
    forceCompleteKarigarJobs,

    // =====================================================================
    // Actions - Status & queue (orderKarigar slice)
    // =====================================================================
    getOrderStatus,
    getQueue,
    checkCanFulfill,

    // =====================================================================
    // Actions - UI
    // =====================================================================
    openAssignmentModal,
    closeAssignmentModal,
    openStatusPanel,
    closeStatusPanel,
    openQueueModal,
    closeQueueModal,

    // =====================================================================
    // Actions - Clear state
    // =====================================================================
    clearAvailableKarigars: clearAvailable,
    clearOrderStatus: clearStatus,
    clearKarigarQueue: clearQueue,
    clearCanFulfillCheck: clearCanFulfillResult,
    clearKarigarUI: clearUI,
    clearError: clearErrorMessage,
    clearSuccessMessage: clearSuccess,
    clearKarigarJobs: clearJobs,
    clearAll: clearAllState,
  };
};

// ============================================================================
// HELPER HOOK: useOrderItemKarigar
// ============================================================================
// Use this when working with a specific order item

export const useOrderItemKarigar = (order, itemIndex) => {
  const {
    getBulkAvailability,
    assignViaOrder,
    reassignForOrderItem,
    createOrderKarigarJob,
    updateOrderKarigarJob,
    cancelOrderKarigarJob,
    bulkProductKarigars,
    bulkOtherKarigars,
    bulkRecommendations,
    bulkSummary,
    loading,
    error,
  } = useOrderKarigar();

  // Get the specific item
  const item = useMemo(
    () => order?.items?.[itemIndex] || null,
    [order, itemIndex]
  );

  // Get existing assignment for this item
  const assignment = useMemo(
    () =>
      order?.karigarAssignments?.find((a) => a.orderItemIndex === itemIndex) ||
      null,
    [order, itemIndex]
  );

  // Check if item has karigar assigned
  const hasKarigar = useMemo(() => !!assignment, [assignment]);

  // Check if item can have karigar assigned
  const canAssign = useMemo(() => {
    if (!order) return false;
    const allowedStatuses = ["DRAFT", "PENDING", "CONFIRMED", "PROCESSING"];
    return allowedStatuses.includes(order.status) && !hasKarigar;
  }, [order, hasKarigar]);

  // Check if karigar can be reassigned
  const canReassign = useMemo(() => {
    if (!assignment) return false;
    const allowedStatuses = ["PENDING", "ASSIGNED", "ACCEPTED"];
    return allowedStatuses.includes(assignment.status);
  }, [assignment]);

  // Load available karigars for this item
  const loadAvailableKarigars = useCallback(() => {
    if (!item || !order) return;

    return getBulkAvailability({
      branchCode: order.branchCode,
      availableBranches: order.availableBranches || [order.branchCode],
      productType:
        item.product?.productType || item.productSnapshot?.productType,
      productSubType:
        item.product?.productSubType || item.productSnapshot?.productSubType,
      workType: "NEW_MAKING",
      complexity:
        item.product?.manufacturing?.complexity ||
        item.productSnapshot?.manufacturing?.complexity ||
        "medium",
      requiredSkills:
        item.product?.manufacturing?.requiredSkills ||
        item.productSnapshot?.manufacturing?.requiredSkills ||
        [],
      targetDate: order.expectedDeliveryDate,
      capableKarigars:
        item.product?.manufacturing?.capableKarigars ||
        item.productSnapshot?.manufacturing?.capableKarigars ||
        [],
      estimatedMakingTime:
        item.product?.manufacturing?.estimatedMakingTime ||
        item.productSnapshot?.manufacturing?.estimatedMakingTime,
      includeOtherKarigars: true,
    });
  }, [item, order, getBulkAvailability]);

  // Assign karigar using orderKarigar slice
  const assign = useCallback(
    (karigarId, workDetails = {}) => {
      if (!order || itemIndex === undefined) return;
      return assignViaOrder(order.orderId, itemIndex, karigarId, workDetails);
    },
    [order, itemIndex, assignViaOrder]
  );

  // Assign karigar using order slice (new route)
  const assignViaOrderRoute = useCallback(
    (karigarId, jobData = {}) => {
      if (!order) return;
      return createOrderKarigarJob(order.orderId, {
        itemIndex,
        karigarId,
        ...jobData,
      });
    },
    [order, itemIndex, createOrderKarigarJob]
  );

  // Reassign karigar
  const reassign = useCallback(
    (newKarigarId, reason) => {
      if (!order || itemIndex === undefined) return;
      return reassignForOrderItem(
        order.orderId,
        itemIndex,
        newKarigarId,
        reason
      );
    },
    [order, itemIndex, reassignForOrderItem]
  );

  // Update job
  const updateJob = useCallback(
    (jobId, updateData) => {
      if (!order) return;
      return updateOrderKarigarJob(order.orderId, jobId, updateData);
    },
    [order, updateOrderKarigarJob]
  );

  // Cancel job
  const cancelJob = useCallback(
    (jobId, reason) => {
      if (!order) return;
      return cancelOrderKarigarJob(order.orderId, jobId, reason);
    },
    [order, cancelOrderKarigarJob]
  );

  return {
    // Item data
    item,
    assignment,
    hasKarigar,
    canAssign,
    canReassign,

    // Available karigars
    productKarigars: bulkProductKarigars,
    otherKarigars: bulkOtherKarigars,
    recommendations: bulkRecommendations,
    summary: bulkSummary,

    // Loading & error
    loading: loading.bulkAvailability || loading.assign || loading.reassign,
    error,

    // Actions
    loadAvailableKarigars,
    assign,
    assignViaOrderRoute,
    reassign,
    updateJob,
    cancelJob,
  };
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================
export default useOrderKarigar;
